import { Context } from '../..';
import * as base from '../base';

const CONTROLLER = 'chat';

/**
 * Chat message interface
 */
export interface ChatMessage {
  id: string;
  role: string;
  content: string;
  isToolUse?: boolean;
}

/**
 * Chat request interface
 */
export interface ChatRequest {
  message: string;
  conversationHistory?: ChatMessage[];
}

/**
 * Chat response interface
 */
export interface ChatResponse {
  message: string;
  conversationHistory: ChatMessage[];
}

/**
 * Sends a chat message and returns the complete response
 * @param context Overmind context
 * @param message The message to send
 * @param conversationHistory Optional conversation history
 * @returns The chat response
 */
export const sendChatMessage = async (context: Context, message: string, conversationHistory?: ChatMessage[]): Promise<ChatResponse> => {
  const url = base.aiURL(CONTROLLER, '');
  const request: base.IRequest = {
    url,
    method: 'POST',
    body: JSON.stringify({
      message,
      conversationHistory,
    }),
  };

  const response: base.IResponse<ChatResponse> = await base.requestWithRetries(request);

  if (response.error) {
    throw new Error(response.error.detail || 'Failed to send chat message');
  }

  return response.data!;
};

/**
 * Creates an EventSource for streaming chat responses
 * @param message The message to send
 * @param conversationHistory Optional conversation history
 * @param onMessage Callback for message events
 * @param onToolUse Callback for tool use events
 * @param onToolResponse Callback for tool response events
 * @param onError Callback for error events
 * @param onDone Callback for done events
 * @param onToken Callback for token events (streaming individual tokens)
 * @returns The EventSource instance
 */
export const createChatStream = (
  message: string,
  onMessage: (data: string) => void,
  onToolUse?: (data: any) => void,
  onToolResponse?: (data: any) => void,
  onError?: (error: any) => void,
  onDone?: () => void,
  onToken?: (token: string) => void
): EventSource => {
  // Get the API URL
  const baseUrl = base.aiURL();

  // Create the URL with parameters
  const url = new URL(`${baseUrl}${CONTROLLER}/stream`);

  // Add query parameter for the message
  // Note: We're only using the message parameter for now since the API is using GET
  // and conversation history would make the URL too long
  url.searchParams.append('message', message);

  // Create the EventSource
  const eventSource = new EventSource(url.toString());

  // Add event listeners
  eventSource.addEventListener('message', event => {
    onMessage(event.data);
  });

  if (onToolUse) {
    eventSource.addEventListener('tool_use', event => {
      onToolUse(JSON.parse(event.data));
    });
  }

  if (onToolResponse) {
    eventSource.addEventListener('tool_response', event => {
      onToolResponse(JSON.parse(event.data));
    });
  }

  if (onError) {
    eventSource.addEventListener('error', event => {
      onError(event);
      eventSource.close();
    });
  }

  if (onDone) {
    eventSource.addEventListener('done', () => {
      eventSource.close();
      onDone();
    });
  }

  // Add token event listener for streaming individual tokens
  if (onToken) {
    eventSource.addEventListener('token', event => {
      onToken(event.data);
    });
  }

  return eventSource;
};
